import { Directive, Input, OnDestroy, inject } from '@angular/core';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { DfEventService } from '@df/core/service/df-event';
import { Gtm } from '@df/module-gtm/event/gtm.event';
import { TimeoutService } from '@df/ng/timeout.service';
import isEqual from 'lodash-es/isEqual';
import sortBy from 'lodash-es/sortBy';
import { GtmItemDirective } from './gtm-item.directive';

/**
 * GTM list directive
 */
@Directive({
    selector: '[gtmList]',
    standalone: true
})
export class GtmListDirective implements OnDestroy {
    /**
     * GTM list data
     */
    @Input({
        required: true,
        transform: (value: string | { listName: string; listId?: string; searchTerm?: string }) => {
            if (typeof value == 'string') {
                return { listName: value };
            }
            return value;
        }
    })
    gtmList!: { listName: string; listId: string; searchTerm?: string; additionalContext?: Record<string, any> };

    /**
     * Optional listing for changes tracking
     */
    @Input()
    gtmListListing?: ProductListing;

    /**
     * Displayed items list
     */
    private items = new Set<GtmItemDirective>();

    /**
     * Timeout service
     */
    private timeoutService = inject(TimeoutService);

    /**
     * Event service
     */
    private dfEventService = inject(DfEventService);

    /**
     * If list is destroyed flag
     */
    private destroyed = false;

    /**
     * Last time tracked data
     */
    private lastDataTracked?: (Gtm.List | Gtm.ListWithState) & { items: Gtm.Item[] };

    register(item: GtmItemDirective) {
        if (!this.items.has(item)) {
            this.items.add(item);
            this.onItemsChange();
        }
    }

    unregister(item: GtmItemDirective) {
        if (this.items.has(item)) {
            this.items.delete(item);
            this.onItemsChange();
        }
    }

    ngOnDestroy() {
        this.destroyed = true;
    }

    /**
     * Debounced method triggering event with items list
     */
    onItemsChange = this.timeoutService.debounce(() => {
        if (this.destroyed) {
            return;
        }

        const items = Array.from(this.items);

        if (!items.length) {
            return;
        }

        const itemsData = sortBy(items.map(i => i.gtmItem, 'index'));

        let data: (Gtm.List | Gtm.ListWithState) & { items: Gtm.Item[] };

        const listing = this.gtmListListing;

        if (listing) {
            data = {
                items: itemsData,
                ...this.gtmList,
                filters: listing.prepareFiltersState(),
                sort: listing.sort?.getState(),
                listing
            };
        } else {
            data = {
                items: itemsData,
                ...this.gtmList
            };
        }
        if (isEqual(this.lastDataTracked, data)) {
            return;
        }

        this.lastDataTracked = data;

        this.dfEventService.dispatch({
            id: 'dfGtmItemListView',
            data
        });
    }, 1000);
}
